import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../common/Logo';
import ThemeToggle from '../common/ThemeToggle';
import { motion, AnimatePresence } from 'framer-motion';

const Icon = ({ name, className = '' }) => (
  <span className={`ms-icon ${className}`}>{name}</span>
);

/* Leading slash so the anchors also work from /login and /register,
   not just from the landing page itself. */
const NAV_LINKS = [['/#new-styles', 'Styles'], ['/#how-it-works', 'How it works'], ['/#pricing', 'Pricing']];

const SiteHeader = () => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="fixed top-0 left-0 right-0 z-40">
      <div className="absolute inset-0 bg-cream-50/85 dark:bg-stone-950/85 backdrop-blur-lg border-b border-stone-200/60 dark:border-stone-800/60" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between relative z-10">
        {/* Already on "/"? The Link is a no-op, so scroll back to the top ourselves.
            Instant scroll: smooth gets silently cancelled by the page's motion animations. */}
        <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2.5 group">
          <Logo className="w-8 h-8 lg:w-9 lg:h-9 group-hover:scale-105 transition-transform" />
          <span className="text-xl lg:text-2xl font-extrabold font-brand leading-none pt-1 text-stone-900 dark:text-stone-100">DesiCaptions</span>
        </Link>

        {/* Absolutely centered so the wider right-side button group can't push it off-center */}
        <nav className="hidden lg:flex items-center gap-6 absolute left-1/2 -translate-x-1/2">
          {NAV_LINKS.map(([href, label]) => (
            href === '/' ? (
              <Link key={href} to={href} onClick={() => window.scrollTo(0, 0)} className="text-[15px] font-semibold font-jakarta text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors">
                {label}
              </Link>
            ) : (
              <a key={href} href={href} className="text-[15px] font-semibold font-jakarta text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors">
                {label}
              </a>
            )
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <ThemeToggle />
          {user ? (
            <Link to="/app" className="h-10 flex items-center whitespace-nowrap bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold font-jakarta px-4 sm:px-5 rounded-xl transition-colors">
              Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="h-10 hidden sm:flex items-center whitespace-nowrap text-sm font-semibold font-jakarta text-stone-600 dark:text-stone-300 px-3 sm:px-4 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-xl transition-colors">
                Login
              </Link>
              <Link to="/register" className="h-10 flex items-center whitespace-nowrap bg-stone-900 hover:bg-black dark:bg-stone-100 dark:hover:bg-white text-white dark:text-stone-900 text-sm font-bold font-jakarta px-4 sm:px-5 rounded-xl transition-colors">
                Try free
              </Link>
            </>
          )}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden tap-target w-10 h-10 flex items-center justify-center rounded-xl text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} className="text-2xl" />
          </button>
        </div>
      </div>

      {/* Mobile menu: the header's blurred backdrop stretches to cover it */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="lg:hidden relative z-10 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-4 flex flex-col border-t border-stone-200/60 dark:border-stone-800/60">
              {NAV_LINKS.map(([href, label]) => (
                href === '/' ? (
                  <Link
                    key={href}
                    to={href}
                    onClick={() => { setMenuOpen(false); window.scrollTo(0, 0); }}
                    className="py-3 px-2 text-[15px] font-semibold font-jakarta text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 border-b border-stone-100/80 dark:border-stone-800/60 last:border-b-0 transition-colors"
                  >
                    {label}
                  </Link>
                ) : (
                  <a
                    key={href}
                    href={href}
                    onClick={() => setMenuOpen(false)}
                    className="py-3 px-2 text-[15px] font-semibold font-jakarta text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 border-b border-stone-100/80 dark:border-stone-800/60 last:border-b-0 transition-colors"
                  >
                    {label}
                  </a>
                )
              ))}
              {!user && (
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="sm:hidden py-3 px-2 text-[15px] font-semibold font-jakarta text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
                >
                  Login
                </Link>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default SiteHeader;
